/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ClusterApiService, ClusterService, NodeModel, PromptService, ServicesApiService } from '../../../shared';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    UserService,
    UtilsService
} from '../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { ServiceModel } from '../../../shared/models/serviceModel';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-hb-conf',
    templateUrl: './haConf.component.html',
    styleUrls: ['./haConf.component.scss'],
})

export class HaConfComponent extends BaseComponent {
    /**
     * To store the table row data.
     */
    public tableRows: any[] = [];

    /**
     * Logged in appliance Ip Address.
     */
    public currentIP: string;

    /**
     * Used to filter nodes in cluster and as a query param.
     */
    public searchTerm: string = '';

    /**
     * Restart required
     */
    public restartRequired: boolean = false;

    /**
     * Store permissions in lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Store action state
     */
    public actionInProgress: boolean = false;

    /**
     * Stored appliance id.
     */
    public applianceId: string;

    /**
     * Filter services
     */
    public filteredServices: ServiceModel;

    constructor(
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private clusterApiService: ClusterApiService,
        private clusterService: ClusterService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private promptService: PromptService,
        private rbacService: RbacService,
        private router: Router,
        private servicesAPI: ServicesApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.subscribeToQueryParams();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Open add node page.
     * @param row
     */
    public openAddNode(row?: { id: any; }): void {
        if (row) {
            this.router.navigateByUrl(this.navigationService.getEnduranceUrl(`/cluster-configuration/ha-config/restore/${row.id}`)).then();
        } else {
            this.router.navigate(['add'], { relativeTo: this.activatedRoute }).then();
        }
    }

    /**
     * Return the overall status of the services, if one services requires a restart then the whole status is red.
     * @param row
     */
    public overallServicesState(row: any): string {
        if (row.services) {
            const servicesStates = Object.values(row.services);
            const restart: boolean = servicesStates.some(item => item['action'] === 'restart');
            if (restart) {
                return 'error';
            }

            const reload: boolean = servicesStates.some(item => item['action'] === 'reload');
            if (reload) {
                return 'warn';
            }

            const active: boolean = servicesStates.some(item => item['status'] === 'active');
            if (active) {
                return 'active';
            }
        } else {
            return 'none';
        }
    }

    /**
     * Submit a cluster reload/restart.
     */
    public clusterRestartReload(): void {
        this.actionInProgress = true;
        this.clusterApiService.apply()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HaConfComponent: clusterRestartReload',
                        'Error when submitting cluster for reload/restart', error);
                },
                complete: (): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.actionInProgress = false;
                }
            });
    }

    /**
     * Confirm remove node.
     * @param row
     */
    public confirmRemoveNode(row: { hostname: any; ip: any; }): void {
        const mainMessage: string = this.languageService.translate('ha_conf_remove_node_message');
        const confirmationObject = {
            title: 'ha_conf_remove_node_title',
            message: `${mainMessage} <br>${row.hostname} - ${row.ip}`,
            closeInternally: false,
            callBack: this.removeNode.bind(this, row),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Submit a cluster re-sync.
     */
    public reSyncCluster(): void {
        this.actionInProgress = true;
        this.clusterApiService.resync()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HaConfComponent: reSyncCluster', 'Error re-syncing cluster', error);
                },
                complete: (): void => {
                    this.loadingService.loadingActionDotsEmitter.next(false);
                    this.actionInProgress = false;
                }
            });
    }

    /**
     * Promote a node from passive to active within a cluster
     * @param row
     */
    public promoteNode(row: { id: any; ip: string; hostname: string; }): void {
        this.actionInProgress = true;
        this.clusterApiService.promote(row.id)
            .pipe(
                takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.notifyService.success(
                        'Node ' + row.hostname + ' was successfully promoted!',
                        { id: 'node-promotion' });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HaConfComponent: checkNodeInChanges',
                        'Error checking for node changes', error);
                },
                complete: (): void => {
                    this.actionInProgress = false;
                }
            });
    }

    /**
     * Open modal to confirm restart service.
     * @param row
     * @param item
     */
    public confirmRestartOnOtherAppliance(row: any, item: any): void {
        if (this.permissionsLookup['ha-configuration-restart-other']) {
            const confirmationObject = {
                title: 'ha_conf_confirm_service_restart',
                message: 'ha_conf_confirm_service_restart_message',
                callBack: this.restartService.bind(this, row, item),
                options: { translateText: true }
            };
            this.modalService.confirmDecision(confirmationObject);
        }
    }

    /**
     * Format the cluster status.
     * @param status
     */
    public showHeartRate(status: string): string {
        const knownStates: string[] = ['active', 'passive', 'suspicious', 'unavailable'];

        return knownStates.includes(status.toLowerCase()) ? status.toLowerCase() : 'No data';
    }

    /**
     * Set the online/offline state of the nodes in the cluster.
     * @param status
     */
    public setNodeState(status: string): string {
        switch (status) {
            case 'active':
            case 'passive':
                return 'Online';
            case 'unavailable':
            case 'suspicious':
                return 'Offline';
            default:
                return 'No data';
        }
    }

    /**
     * Link to system services page.
     * @param service
     */
    public linkToServices(service: any): void {
        if (this.permissionsLookup['ha-configuration-services-link']) {
            const url: string = this.navigationService.getEnduranceUrl('/maintenance/services');
            this.router.navigate([url], { queryParams: { sFilter: service } }).then().catch();
        }
    }

    /**
     * Restart service.
     * @param row
     * @param item
     * @private
     */
    private restartService(row: { id: any; }, item: { key: string; }): Observable<any> {
        return new Observable((observer) => {
            this.servicesAPI.restartService(item.key, { applianceId: row.id })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('HaConfComponent: restartService', 'Error restarting service', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Submit a remove node.
     * @param row
     * @private
     */
    private removeNode(row: { id: any; ip: string; }): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.clusterApiService.removeNode(row.id, { timeout: 15000 })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.clusterService.removeNode(row.id);

                        this.loadingService.loadingActionDotsEmitter.next(false);
                        this.modalService.close('confirmation-box');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('HaConfComponent: removeNode', 'Error removing node', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Subscribe to inbound search query.
     * Note: Using a subscriber here because the notification can contain a link that can be actioned
     * by the user after the page has loaded.
     * @private
     */
    private subscribeToQueryParams(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: any): void => {
                    if (params?.sFilter) {
                        this.searchTerm = this.utilsService.duplicateObject(params.sFilter);
                        this.navigationService.removeParamsFromUrl();
                    }
                }
            });
    }

    /**
     * Set-up page data and get the nodes status from the cluster API service
     * @private
     */
    private setUpPageData(): void {
        const nodes: NodeModel[] = this.clusterService.nodes;
        this.populateTableRows(nodes);

        this.restartRequired = this.nodeRequiresReloadRestart(nodes);
        this.currentIP = this.userService.getUser('applianceAddress');
        this.applianceId = this.userService.getUser('applianceId');

        this.setUpNodeSubscriber();

        this.filteredServices = this.filteredNodeServices(nodes);
        this.setUpServiceStatusSubscriber();
    }

    /**
     * Subscribe to nodes updates.
     * @private
     */
    private setUpNodeSubscriber(): void {
        this.clusterService.nodesEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (nodes: NodeModel[]): void => {
                    this.populateTableRows(nodes);
                    this.restartRequired = this.nodeRequiresReloadRestart(nodes);
                    this.cdr.detectChanges();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HaConfComponent: setUpSubscribers', 'Error in setUpSubscribers', error);
                }
            });
    }

    /**
     * Subscribe to service status updates.
     * @private
     */
    private setUpServiceStatusSubscriber(): void {
        this.promptService.serviceStatusEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (nodes: any): void => {
                    this.filteredServices = this.filteredNodeServices(nodes);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('HaConfComponent: setUpSubscribers', 'Error in setUpSubscribers', error);
                }
            });
    }

    /**
     * Filtered node services.
     * @param nodes
     * @private
     */
    private filteredNodeServices(nodes: any[]) {
        const filteredNodeServices: any = {};
        const actionsToShow: string[] = ['reload', 'restart', 'start'];
        if (nodes?.length) {
            for (const node of nodes) {
                filteredNodeServices[node.id] = node.services?.filter((item: any) => actionsToShow.includes(item['action']));
            }
        }

        return filteredNodeServices;
    }

    /**
     * Populate table rows for displaying in the table.
     * @param nodes
     * @private
     */
    private populateTableRows(nodes: NodeModel[]): void {
        this.tableRows = [];
        for (const node of nodes) {
            this.tableRows.push({
                id: node.id,
                hostname: node.hostname,
                ip: node.ip,
                port: node.port,
                latency: node.latency,
                services: node.services,
                status: node.status,
                state: this.setNodeState(node.status),
                lastReceived: node.lastReceived
            });
        }
    }

    /**
     * Check if any services require a reload or a restart.
     * @param nodes
     * @private
     */
    private nodeRequiresReloadRestart(nodes: NodeModel[]): boolean {
        for (const node of nodes) {
            if (node.services?.length) {
                for (const service of node.services) {
                    if (service.action === 'reload' || service.action === 'restart') {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'ha-configuration-add-node': [{ path: '/cluster/join', methods: ['POST'] }],
            'ha-configuration-restore-node': [{ path: '/cluster/restore', methods: ['POST'] }],
            'ha-configuration-resync': [{ path: '/cluster/resync', methods: ['PUT'] }],
            'ha-configuration-promote-node': [{ path: '/cluster/promote/:id', methods: ['GET'] }],
            'ha-configuration-remove-node': [{ path: '/cluster/:id', methods: ['DELETE'] }],
            'ha-configuration-reload-restart': [{ path: '/cluster/services-apply', methods: ['PUT'] }],
            'ha-configuration-restart-other': [{ path: '/services/:service/restart', methods: ['PUT'] }],
            'ha-configuration-services-link': [{ path: '/services', methods: ['GET'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
